import {HOTB} from "../HOTB";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {GameAssets} from "../GameAssets";
import {HOTBData, HOTBGameData} from "../server/HOTBData";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {GameSounds} from "../sound/GameSounds";
import {SimplePresentationRunnerController} from "../../core/presentation/SimplePresentationRunner";
import {GameUtils} from "../utils/GameUtils";
import {ExtendedGameModel} from "../server/ExtendedGameModel";

export class FSOutroView extends PIXI.Container implements StageLayer {
    private _amountText: BasicBitmapCountUp;
    private _outroText: PIXI.Text;
    private _fontStyle: any = {
        fontName: GameAssets.BITMAP_FONT_FS_INTRO_OUTRO,
        fontSize: 26
    };
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _outroController: SimplePresentationRunnerController | undefined;

    constructor() {
        super();
        this.name = "FSOutroView";
        this.init();
        this.onResize();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public showOutro(callback: () => void, controller?: SimplePresentationRunnerController): void {
        if (controller) {
            this._outroController = controller;
        }

        const tl: gsap.core.Timeline = gsap.timeline(),
            gameData: HOTBGameData = HOTB.model.gameData;

        this.updateDynamicContent(gameData.accumulatedRoundWin, gameData.numberOfFreespinsPlayed);

        tl.to(this, {duration: 1});
        tl.add([
            HOTB.gameForeground.handleDoors(false, false),
            (): void => {
                HOTB.soundPlayer.fadeMusic(0, 100);

                if (gameData.mode === ExtendedGameMode.FREESPIN || gameData.mode === ExtendedGameMode.FREESPIN_AVALANCHE || (gameData.freespinsLeft === 0 && gameData.numberOfFreespinsPlayed === 8)) {
                    HOTB.soundPlayer.playLoopEffect(GameSounds.FS_OUTRO_LOOP);
                } else {
                    HOTB.soundPlayer.playLoopEffect(GameSounds.SFS_OUTRO_LOOP);
                }
            }
        ]);

        tl.add((): void => {
            const featurePrice: number = GameUtils.getBuyFeaturePrice();
            if(HOTB.model.data.accumulatedRoundWin > featurePrice){
                HOTB.soundPlayer.playSound(GameSounds.BONUS_OUTRO_LARGE_END);
            } else {
                if(!GameUtils.isWinBelowStake()) {
                    HOTB.soundPlayer.playSound(GameSounds.BONUS_OUTRO_END);
                }
            }

            this._clickCallback = callback;

            this._button.alpha = 0;
            this._amountText.alpha = 0;
            this._outroText.alpha = 0;

            this._button.visible = true;
            this._amountText.visible = true;
            this._outroText.visible = true;
        });

        tl.to([this._button, this._amountText, this._outroText], {
            duration: 0.5,
            alpha: 1
        });

        tl.add((): void => {
            this._button.enable(true);
        });
    }

    private updateDynamicContent(totalWinAmount: number, totalSpinsPlayed: number): void {
        this._amountText.getCountAnimation(totalWinAmount, totalWinAmount, 0.001);

        this._outroText.text = HOTB.apiPlugin.translations.translate("Spins played: ") + totalSpinsPlayed;

        while (this._outroText.width > 400) {
            this._outroText.style.fontSize--;
        }
    }

    private init(): void {
        this.createTotalAmountText();
        this.createSpinsPlayedText();
        this.createButton();
    }

    private createTotalAmountText(): void {
        this._amountText = new BasicBitmapCountUp(this._fontStyle, 390, new PIXI.Point(0.5, 0.5));
        this._amountText.position.set(357, 450);
        this._amountText.visible = false;

        this.addChild(this._amountText);
    }

    private createSpinsPlayedText(): void {
        this._outroText = new PIXI.Text("", {
            fontName: "OpenSans-Bold",
            fontSize: 50,
            fill: "#FFFFFF",
            stroke: "#000000",
            strokeThickness: 10,
        });
        this._outroText.position.set(360, 540);
        this._outroText.anchor.set(0.5, 0.5);
        this._outroText.visible = false;

        this.addChild(this._outroText);
    }

    private createButton(): void {
        this._button = new ContinueButton();
        this._button.addClickCallback((): void => {
            this.onButtonClick();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false)
        this._button.visible = false;

        this.addChild(this._button);
    }

    private onButtonClick(): void {
        this._button.enable(false);
        HOTB.soundPlayer.playSound(GameSounds.CONTINUE_BUTTON);
        HOTB.model.isPlayingBonusSpins = false;

        const tl: gsap.core.Timeline = gsap.timeline(),
            duration: number = 0.5,
            gameModel: ExtendedGameModel = HOTB.model;

        if (this._outroController) {
            this._outroController.setSkipCallback((): void => {
                tl.seek("skipTransition", false);
            });
        }

        tl.to([this._button, this._amountText, this._outroText], {
            duration: duration,
            alpha: 0
        }, 0);

        tl.add((): void => {
            this._button.visible = false;
            this._amountText.visible = false;
            this._outroText.visible = false;
        });

        tl.add(HOTB.gameBackground.playBGTransition());

        tl.add((): void => {
            if (this._outroController) {
                this._outroController.setSkipCallback();
                this._outroController = undefined;
            }
        });

        tl.addLabel("skipTransition");

        tl.add([
            HOTB.gameForeground.handleDoors(true, true),
            (): void => {
                HOTB.soundPlayer.switchMusic(HOTB.model.gameData.nextMode);

                if (gameModel.data.mode.includes(ExtendedGameMode.FREESPIN_SUPER) || gameModel.gameData.numberOfFreespinsPlayed > 8) {
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.SFS_OUTRO_LOOP);
                } else {
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.FS_OUTRO_LOOP);
                }
            }
        ], (tl.duration() + 0.05));

        tl.add((): void => {
            this._clickCallback();
        });
    }
}